'use client';

import Slider, { SliderValueField } from '@/components/ui/Slider';
import {
  displayLengthEditString,
  metersFromDisplay,
  snapLengthToStep,
  type LengthUnit,
} from '@/lib/tools/units';
import { clamp } from '@/lib/tools/roomLayout';

interface Props {
  label: string;
  valueM: number;
  minM: number;
  maxM: number;
  /** Slider-drag and arrow-key step, measured in the DISPLAYED unit: 0.1 is
   *  0.1 m in metric and 0.1 ft in imperial. Typed values are never snapped —
   *  only drags/keys round, and only to the visible unit's grid. */
  stepM?: number;
  unit: LengthUnit;
  onChangeM: (meters: number) => void;
  /** Optional test id for the numeric field. */
  testId?: string;
}

/**
 * The value beside the track: ink at the label's size, tabular, the unit
 * beside it at the same size. A hairline under it on hover says it can be
 * typed into, and a solid one while it is.
 *
 * It used to be bold accent text at Tailwind's stock 12px, a size with no role
 * behind it, and smaller than its unit on the /tools pages. A value is a
 * readout, and a readout never wears the accent — that marks the current
 * position in a flow and nothing else (AGENTS.md's Status-readout rule) — and
 * type comes only from the scale.
 */
const VALUE =
  'w-[4.5rem] border-b border-transparent bg-transparent text-right font-mono text-body-sm '
  + 'text-[var(--rt-ink)] outline-none hover:border-[var(--rt-hairline-strong)] focus:border-[var(--rt-ink)]';
const UNIT = 'text-body-sm text-[var(--rt-ink-dim)]';

/** Dimension slider for the public calculators — value in meters; display
 *  follows unit and is editable. The editor's own length control is
 *  room/shared/DimensionField. Track/thumb/drag/click-to-jump/keyboard
 *  mechanics live in `web/src/components/ui/Slider.tsx` (AGENTS.md's "sliders
 *  must always use ui/Slider.tsx" rule) — this file only owns the
 *  meters<->display-unit conversion. */
export default function ToolDimSlider({
  label,
  valueM,
  minM,
  maxM,
  stepM = 0.05,
  unit,
  onChangeM,
  testId,
}: Props) {
  const clampM = (meters: number) => clamp(meters, minM, maxM);

  // Drags/keys snap to the visible unit's grid; typed commits and unit
  // switches keep full meter precision (the readout truncates for display).
  const snapM = (meters: number) => snapLengthToStep(clampM(meters), stepM, unit);
  // Arrow keys move one visible step: convert back to meters for the
  // primitive, which works in value space.
  const stepValue = unit === 'ft' ? metersFromDisplay(stepM, 'ft') : stepM;

  const formatDisplay = (m: number) => displayLengthEditString(m, unit);

  const parseDisplay = (raw: string): number | null => {
    const n = parseFloat(raw.replace(',', '.'));
    return Number.isFinite(n) ? metersFromDisplay(n, unit) : null;
  };

  return (
    <Slider
      label={label}
      min={minM}
      max={maxM}
      step={stepValue}
      snap={snapM}
      color="var(--rt-accent)"
      thumbs={[
        {
          value: valueM,
          onChange: (m) => onChangeM(clampM(m)),
          ariaLabel: `${label} value`,
          // The draggable thumb and the click-to-edit readout are two
          // separate DOM nodes — give them distinct testids so
          // `getByTestId(testId)` (the readout, what e2e reads the achieved
          // value from) doesn't collide with the drag handle.
          testId: testId ? `${testId}-thumb` : undefined,
          format: formatDisplay,
          parse: parseDisplay,
        },
      ]}
      renderReadout={(thumbs) => {
        const t = thumbs[0]!;
        return (
          <span className="inline-flex items-center gap-1">
            <SliderValueField
              value={t.value}
              onCommit={(m) => onChangeM(clampM(m))}
              format={formatDisplay}
              parse={parseDisplay}
              min={minM}
              max={maxM}
              ariaLabel={t.ariaLabel}
              testId={testId}
              className={VALUE}
            />
            <span className={UNIT}>{unit}</span>
          </span>
        );
      }}
    />
  );
}
